import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Navigation, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

/**
 * App-wide navigation helper. ONE canonical place that turns a service address
 * into an Apple Maps / Google Maps launch.
 *
 * Usage:
 *   const { openAddress } = useMapsNav();
 *   openAddress(job.address);
 *
 * Or the convenience component:
 *   <AddressLink address={job.address} testId="preview-address-link" />
 *
 * Behaviour is device-aware and honours the per-user `maps_preference`
 * (ask | apple | google) surfaced on the auth user. It is intentionally
 * generic (no company/role assumptions) so it works for any future tenant.
 */

const MapsNavContext = createContext({ openAddress: () => {} });
export const useMapsNav = () => useContext(MapsNavContext);

const _ua = () => (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');

// iOS (incl. iPadOS Safari that reports as Mac + touch), Android, Mac desktop.
export function detectPlatform() {
  const s = _ua();
  const isIOS =
    /iPad|iPhone|iPod/.test(s) ||
    (/Macintosh/.test(s) && typeof document !== 'undefined' && 'ontouchend' in document);
  const isAndroid = /Android/.test(s);
  const isMobile = isIOS || isAndroid;
  return { isIOS, isAndroid, isMobile, isDesktop: !isMobile };
}

// Apple Maps universal link → native app on Apple devices, web elsewhere.
// Google Maps universal directions URL → app if installed, else browser tab.
export function buildMapsUrl(provider, address) {
  const dest = encodeURIComponent((address || '').trim());
  if (provider === 'apple') return `https://maps.apple.com/?daddr=${dest}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
}

function launch(url) {
  try {
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (!w) window.location.href = url;
  } catch {
    window.location.href = url;
  }
}

export const MapsNavProvider = ({ children }) => {
  const { user } = useAuth();
  const [sheet, setSheet] = useState({ open: false, address: '', showApple: false });
  const launchingRef = useRef(false);   // guards against rapid double-tap launching twice

  // Background scroll-lock while the chooser is open. Without this the underlying
  // schedule keeps scrolling and consumes the tap as a scroll gesture (the exact
  // "rapid-fire tap until one registers" symptom). Restores exactly on close.
  useEffect(() => {
    if (!sheet.open) return;
    const { body } = document;
    const scrollY = window.scrollY;
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [sheet.open]);

  const openAddress = useCallback(
    (address) => {
      const addr = (address || '').trim();
      if (!addr) {
        toast.error('No service address on file for directions.');
        return;
      }
      const platform = detectPlatform();
      const pref = (user && user.maps_preference) || 'ask';

      if (pref === 'google') {
        launch(buildMapsUrl('google', addr));
        return;
      }
      if (pref === 'apple') {
        // Apple Maps isn't available on Android → gracefully fall back to Google.
        launch(buildMapsUrl(platform.isAndroid ? 'google' : 'apple', addr));
        return;
      }
      // Ask Every Time — Apple option hidden on Android per requirements.
      launchingRef.current = false;
      setSheet({ open: true, address: addr, showApple: !platform.isAndroid });
    },
    [user]
  );

  const close = useCallback(() => setSheet((s) => ({ ...s, open: false })), []);
  const pick = useCallback(
    (provider) => {
      // Single-fire guard: once a provider is chosen, ignore further taps so a
      // rapid double-tap never launches Maps twice.
      if (launchingRef.current) return;
      launchingRef.current = true;
      launch(buildMapsUrl(provider, sheet.address));
      close();
    },
    [sheet.address, close]
  );

  const value = useMemo(() => ({ openAddress }), [openAddress]);

  return (
    <MapsNavContext.Provider value={value}>
      {children}
      {sheet.open && createPortal(
        <div
          data-testid="maps-nav-sheet"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center animate-in fade-in duration-150"
          style={{ touchAction: 'none', pointerEvents: 'auto' }}
          onClick={close}
          onTouchMove={(e) => e.preventDefault()}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative w-full sm:max-w-sm bg-[#141414] border border-[#262626] rounded-t-2xl sm:rounded-2xl p-4 pb-6 shadow-2xl select-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-[#D4AF37]" /> Open in Maps
                </p>
                <p className="text-[#737373] text-xs mt-1 line-clamp-2">{sheet.address}</p>
              </div>
              <button
                type="button"
                data-testid="maps-nav-close"
                onClick={(e) => { e.stopPropagation(); close(); }}
                aria-label="Close"
                className="flex items-center justify-center w-11 h-11 -mr-2 -mt-2 rounded-full text-[#737373] hover:text-white active:bg-white/10 transition-colors touch-manipulation"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2.5">
              {sheet.showApple && (
                <button
                  type="button"
                  data-testid="maps-nav-apple"
                  onClick={(e) => { e.stopPropagation(); pick('apple'); }}
                  className="w-full min-h-[56px] flex items-center gap-3 px-4 py-3 rounded-xl border border-[#262626] bg-[#0A0A0A] text-white hover:border-[#D4AF37] active:bg-[#1f1f1f] transition-colors touch-manipulation"
                >
                  <MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-base font-medium">Apple Maps</span>
                </button>
              )}
              <button
                type="button"
                data-testid="maps-nav-google"
                onClick={(e) => { e.stopPropagation(); pick('google'); }}
                className="w-full min-h-[56px] flex items-center gap-3 px-4 py-3 rounded-xl border border-[#262626] bg-[#0A0A0A] text-white hover:border-[#D4AF37] active:bg-[#1f1f1f] transition-colors touch-manipulation"
              >
                <MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-base font-medium">Google Maps</span>
              </button>
              <button
                type="button"
                data-testid="maps-nav-cancel"
                onClick={(e) => { e.stopPropagation(); close(); }}
                className="w-full min-h-[48px] flex items-center justify-center rounded-xl border border-[#262626] text-[#A3A3A3] hover:text-white active:bg-white/5 text-sm font-medium transition-colors touch-manipulation"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </MapsNavContext.Provider>
  );
};

/**
 * Convenience clickable address. Renders nothing when there's no address so
 * layouts never show an empty/broken directions link.
 */
export const AddressLink = ({
  address,
  testId,
  className = '',
  iconClassName = 'w-3.5 h-3.5 flex-shrink-0',
  showIcon = true,
  children,
  stopPropagation = true,
  ...rest
}) => {
  const { openAddress } = useMapsNav();
  const addr = (address || '').trim();
  if (!addr) return null;
  const handle = (e) => {
    if (stopPropagation) e.stopPropagation();
    openAddress(addr);
  };
  return (
    <span
      role="button"
      tabIndex={0}
      data-testid={testId}
      onClick={handle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handle(e);
        }
      }}
      title="Get directions"
      className={`inline-flex items-center gap-1.5 text-left hover:text-[#D4AF37] transition-colors cursor-pointer ${className}`}
      {...rest}
    >
      {showIcon && <MapPin className={iconClassName} />}
      <span>{children || addr}</span>
    </span>
  );
};

export default MapsNavProvider;
